import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { Beforeunload } from "react-beforeunload";

type UnsavedChangesContextValue = {
  updateFormState: (formId: string, isDirty: boolean) => void;
  unregisterForm: (formId: string) => void;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export default function UnsavedChangesProvider({ children }: { children?: ReactNode }) {
  const [registeredFormStates, setRegisteredFormStates] = useState<Record<string, boolean>>({});

  const updateFormState = (formId: string, isDirty: boolean) => {
    setRegisteredFormStates((prevState) => {
      let newState = { ...prevState };
      newState[formId] = isDirty;
      return newState;
    });
  };

  const unregisterForm = (formId: string) => {
    setRegisteredFormStates(prevState => {
      if (!(formId in prevState)) {
        return prevState;
      }
      const newState = { ...prevState };
      delete newState[formId];

      return newState;
    });
  }

  const data = {
    updateFormState,
    unregisterForm,
  };

  const someFormIsDirty = Object.values(registeredFormStates).some(Boolean);

  return (
    <UnsavedChangesContext.Provider value={data}>
      {someFormIsDirty && (
        <Beforeunload
          onBeforeunload={() => {
            return "";
          }}
        />
      )}
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export const useUnsavedChanges = (formId: string, isDirty: boolean) => {
  const unsavedChangesContext = useContext(UnsavedChangesContext);

  if (unsavedChangesContext === null) {
    throw new Error("useUnsavedChanges can only be used inside UnsavedChangesProvider");
  }

  const { updateFormState, unregisterForm } = unsavedChangesContext;

  useEffect(() => {
    updateFormState(formId, isDirty);
  }, [isDirty]);

  useEffect(() => {
    return () => {
      unregisterForm(formId);
    };
  }, []);
};

